'use client';

import { useCallback } from 'react';
import {
  formulaTemplateToSource,
  type FormulaArg,
  type FormulaLeaf,
  type FormulaTemplate,
} from '@capture/shared';
import { ALL_FUNCTIONS, FUNCTION_CATEGORIES } from '../shared/formula-lang';

type FunctionTemplate = Extract<FormulaTemplate, { kind: 'function' }>;

interface Props {
  template: FunctionTemplate;
  fieldNames: string[];
  onChange: (source: string) => void;
  scope?: 'record' | 'batch';
}

export function FunctionEditor({ template, fieldNames, onChange, scope }: Props) {
  const batchFns = FUNCTION_CATEGORIES['Batch Aggregation'] ?? [];
  const fnOptions =
    scope === 'record'
      ? [...ALL_FUNCTIONS].filter((f) => !batchFns.includes(f))
      : [...ALL_FUNCTIONS];

  const emit = useCallback(
    (next: FunctionTemplate) => onChange(formulaTemplateToSource(next)),
    [onChange],
  );

  const updateArg = (i: number, arg: FormulaArg) => {
    const args = template.args.slice();
    args[i] = arg;
    emit({ ...template, args });
  };

  const leafControl = (
    a: FormulaLeaf,
    onLeaf: (l: FormulaLeaf) => void,
    label: string,
  ) =>
    a.type === 'field' ? (
      <select
        aria-label={label}
        value={a.name}
        onChange={(e) => onLeaf({ type: 'field', name: e.target.value })}
      >
        {fieldNames.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    ) : (
      <input
        aria-label={label}
        value={a.value}
        onChange={(e) => onLeaf({ type: 'literal', value: e.target.value })}
      />
    );

  return (
    <div className="function-editor">
      <select
        aria-label="Function"
        value={template.fn}
        onChange={(e) => emit({ ...template, fn: e.target.value })}
      >
        {fnOptions.map((f) => (
          <option key={f} value={f}>{f}</option>
        ))}
      </select>
      <span>(</span>
      {/* key={i} is positional and safe ONLY because there is no add/remove/reorder
          arg UI (by design — Code mode reshapes). Do not "fix" to a stable id. */}
      {template.args.map((a, i) => (
        <span key={i} className="function-editor__arg">
          {a.type === 'call' ? (
            <span className="function-editor__nested">
              {a.fn}(
              {a.args.map((l, j) => (
                <span key={j}>
                  {leafControl(
                    l,
                    (nl) => {
                      const inner = a.args.slice();
                      inner[j] = nl;
                      updateArg(i, { ...a, args: inner });
                    },
                    `Argument ${i + 1}, inner ${j + 1}`,
                  )}
                </span>
              ))}
              )
            </span>
          ) : (
            leafControl(a, (l) => updateArg(i, l), `Argument ${i + 1}`)
          )}
          {i < template.args.length - 1 ? ', ' : ''}
        </span>
      ))}
      <span>)</span>
    </div>
  );
}
